import React, { ReactElement } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Chip } from "@mui/material";
import { IdNamePair } from "../models";
import ExtraChipsMessage from "./forms/messages/extraChipsMessage";

import uuid from "uuid";
import { EmptyChipDetails } from "./models";

interface IChipListProps {
    items: IChipListItem<string, string>[];
    truncateAtIndex?: number; // exclusive index
    closable?: boolean;
    onClose?: (item: IdNamePair<string, string>) => void;
    emptyChipDetails?: EmptyChipDetails;
    chipStyle?: React.CSSProperties;
}

export interface IChipListItem<TKey, TName> extends IdNamePair<TKey, TName> {
    disabled?: boolean;
    labelStyle?: React.CSSProperties;
}

const defaultChipStyle: React.CSSProperties = {
    marginRight: "8px",
    marginBottom: "4px",
    backgroundColor: "#e0e0e0", //Background color prevents background change on hover.
    fontSize: 16,
    width: "fit-content"
};

const defaultEmptyChipStyle: React.CSSProperties = {
    ...defaultChipStyle,
    fontStyle: "italic",
    color: "#808080",
};

const ChipList: React.FunctionComponent<IChipListProps> = ({
    items,
    chipStyle,
    truncateAtIndex,
    closable = false,
    onClose,
    emptyChipDetails
}) => {
    const id = uuid();

    const showEmptyChip =  emptyChipDetails?.show ?? false;
    const emptyContents =  emptyChipDetails?.contents ?? "No Topics Selected";

    const getExtraNames = (): string => {
        if (!truncateAtIndex) return "";
        let result = "";

        if (items.length > truncateAtIndex) {
            let remainingItemNames = items.slice(truncateAtIndex).map(item => item.name);
            result = remainingItemNames.join(", ");
        }

        return result;
    };

    const getDefaultChip = (item: IChipListItem<string, string>): ReactElement => {
        return <Chip
            key={item.id}
            label={
                <React.Fragment>
                    <span style={item.labelStyle}>{item.name}</span>
                </React.Fragment>
            }
            style={chipStyle || defaultChipStyle}
            onDelete={setupClose ? () => onClose!(item) : undefined}
            deleteIcon={setupClose ? <CloseIcon /> : undefined}
            disabled={item.disabled}
        />;
    };

    const setupClose = closable && onClose !== undefined;

    return (
        <>
            {
                showEmptyChip && items.length === 0 &&
                <Chip
                    key={`${id}-empty`}
                    label={emptyContents}
                    style={{ ...defaultEmptyChipStyle, ...emptyChipDetails?.styling }}
                />
            }
            {
                items.slice(0, truncateAtIndex).map((item) => (getDefaultChip(item)))
            }
            {
                truncateAtIndex && items.length > truncateAtIndex &&
                <ExtraChipsMessage chipText={`+${items.length - truncateAtIndex}`} hoverText={getExtraNames()} />
            }
        </>
    );
};

export default ChipList;
